import { motion } from "framer-motion";
import { ArrowUpRight, Landmark, Scale, Sprout } from "lucide-react";
import { TID } from "@/lib/testIds";
import { BorderBeam } from "@/components/magicui/border-beam";
import TextReveal from "./TextReveal";

const SERVICES = [
  {
    n: "1",
    icon: Landmark,
    title: "Government Subsidies & Grants",
    tagline: "Central + state incentives, end-to-end.",
    body:
      "We help MSMEs and larger industrial units identify and secure central and state government incentives — capital subsidies, SGST reimbursement, stamp duty and electricity duty exemptions, employment generation subsidies — under policies like Make in Haryana Industrial Policy, PADMA (Cluster Infrastructure Development Scheme), and the Atmanirbhar Textile Policy.",
    bullets: [
      "Eligibility & scheme mapping",
      "DPR / concept note preparation",
      "Application filing & follow-ups",
      "Sanction to disbursement tracking",
    ],
    img: "https://images.pexels.com/photos/4386366/pexels-photo-4386366.jpeg",
  },
  {
    n: "2",
    icon: Scale,
    title: "MSME Arbitration & Bad-Debt Recovery",
    tagline: "MSEFC filings under the MSMED Act, 2006.",
    body:
      "Delayed payments are one of the biggest threats to MSME survival. We represent clients before the Micro and Small Enterprise Facilitation Council (MSEFC) under the MSMED Act, 2006, and assist with filings through the MSME Samadhaan portal — helping businesses recover what they're legally owed, faster and without prolonged litigation.",
    bullets: [
      "MSEFC representation",
      "MSME Samadhaan filings",
      "Statutory interest computation",
      "Settlement & award enforcement",
    ],
    img: "https://images.pexels.com/photos/5668473/pexels-photo-5668473.jpeg",
  },
  {
    n: "3",
    icon: Sprout,
    title: "Project Funding",
    tagline: "DPRs, term loans and policy-linked funding.",
    body:
      "We help businesses structure funding for new and expansion projects — from detailed project reports (DPRs) to identifying the right mix of term loans, government-linked schemes (MSME / PMEGP / CGTMSE routes), and policy-linked incentives that improve project viability.",
    bullets: [
      "Detailed Project Reports",
      "PMEGP · CGTMSE structuring",
      "Bank & NBFC term-loan syndication",
      "Policy incentive stacking",
    ],
    img: "https://images.pexels.com/photos/834892/pexels-photo-834892.jpeg",
  },
];

export const Services = () => (
  <section
    id="services"
    data-testid={TID.services.root}
    className="relative bg-paper py-24 md:py-32 overflow-hidden"
  >
    {/* Brand ambient orbs */}
    <div aria-hidden className="absolute top-32 -right-56 h-[520px] w-[520px] rounded-full bg-[color:var(--violet)]/10 blur-[160px]" />
    <div aria-hidden className="absolute bottom-40 -left-40 h-[420px] w-[420px] rounded-full bg-[color:var(--gold)]/10 blur-[140px]" />

    <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12">
      <div className="grid grid-cols-12 gap-6 pb-14 md:pb-20 border-b border-navy/10">
        <div className="col-span-12 md:col-span-4">
          <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-navy/60">
            02 — What we do
          </p>
        </div>
        <div className="col-span-12 md:col-span-8">
          <TextReveal as="h2" className="font-serif tracking-tight text-4xl md:text-6xl leading-[1.02] text-navy">
            Three practices. One outcome — money in your account.
          </TextReveal>
        </div>
      </div>

      <div className="mt-16 md:mt-24 space-y-24 md:space-y-28">
        {SERVICES.map((s, i) => {
          const Icon = s.icon;
          return (
            <motion.article
              key={s.n}
              data-testid={TID.services.card(s.n)}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10% 0px" }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="grid grid-cols-12 gap-6 md:gap-10 items-start"
            >
              <div className="col-span-12 md:col-span-4">
                <div className="sticky top-28 flex items-start gap-6">
                  <span
                    className="font-serif italic text-[80px] md:text-[120px] leading-none tracking-tighter"
                    style={{ color: "rgba(6,19,41,0.10)" }}
                  >
                    {s.n}
                  </span>
                  <div className="grid h-14 w-14 place-items-center border border-navy/20 text-navy">
                    <Icon className="h-6 w-6" strokeWidth={1.5} />
                  </div>
                </div>
              </div>

              <div className="col-span-12 md:col-span-5">
                <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-navy/50">
                  <TextReveal>{s.tagline}</TextReveal>
                </p>
                <h3 className="mt-3 font-serif tracking-tight text-3xl md:text-[42px] text-navy leading-[1.05]">
                  <TextReveal>{s.title}</TextReveal>
                </h3>
                <p className="mt-6 text-navy/75 text-[15px] leading-relaxed max-w-lg">
                  <TextReveal>{s.body}</TextReveal>
                </p>
                <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-3 text-navy">
                      <span className="mt-2 h-1.5 w-1.5 bg-[color:var(--gold)] flex-shrink-0" />
                      <span className="text-[14px]">{b}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href="#contact"
                  className="mt-8 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.24em] text-navy border-b border-navy/40 pb-1 hover:text-[color:var(--gold)] hover:border-[color:var(--gold)] transition-colors"
                >
                  Start with this <ArrowUpRight className="h-3.5 w-3.5" />
                </a>
              </div>

              <div className="col-span-12 md:col-span-3">
                <div className="relative">
                  <div className="absolute -inset-3 border border-[color:var(--gold)]/40 pointer-events-none" aria-hidden />
                  <div className="relative overflow-hidden">
                    <img
                      src={s.img}
                      alt=""
                      className="clip-frame w-full aspect-[3/4] object-cover grayscale-[30%] hover:grayscale-0 transition-all duration-700"
                    />
                    <BorderBeam
                      duration={10 + i * 2}
                      size={160}
                      colorFrom="#D4AF37"
                      colorTo="#5B2AB0"
                      delay={i * 3}
                    />
                  </div>
                </div>
              </div>
            </motion.article>
          );
        })}
      </div>
    </div>
  </section>
);

export default Services;
